import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate, useParams } from "react-router-dom";
import { LanguageProvider } from "./context/LanguageContext";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Header } from "./components/layout/Header";
import { Footer } from "./components/layout/Footer";
import { WhatsAppButton } from "./components/common/WhatsAppButton";
import { CookieBanner } from "./components/common/CookieBanner";
import { Toaster } from "./components/ui/sonner";
import { Loader2 } from "lucide-react";
import ScrollToTop from "./components/ScrollToTop";

// Pages
import HomePage from "./pages/HomePage";
import ElCampPage from "./pages/ElCampPage";
import CompeticioPage from "./pages/CompeticioPage";
import ContactePage from "./pages/ContactePage";
import MembresPage from "./pages/MembresPage";
import FesTeAbonatPage from "./pages/FesTeAbonatPage";
import AcademiaPage from "./pages/AcademiaPage";
import LegalPage from "./pages/LegalPage";
import NotFoundPage from "./pages/NotFoundPage";
import {
  JugarPage,
  DrivingRangePage,
  RestaurantPage,
  AllotjamentPage,
  CorrespondenciesPage,
  EquipPage,
} from "./pages/SectionPages";

// Admin Pages
import AdminLogin from "./pages/admin/AdminLogin";
import AuthCallback from "./pages/admin/AuthCallback";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminPages from "./pages/admin/AdminPages";
import PageEditor from "./pages/admin/PageEditor";
import AdminCarousel from "./pages/admin/AdminCarousel";
import AdminHomeContent from "./pages/admin/AdminHomeContent";
import AdminSiteConfig from "./pages/admin/AdminSiteConfig";
import AdminSections from "./pages/admin/AdminSections";
import AdminSectionEditor from "./pages/admin/AdminSectionEditor";
import AdminHoles from "./pages/admin/AdminHoles";
import AdminDocuments from "./pages/admin/AdminDocuments";
import AdminMedia from "./pages/admin/AdminMedia";
import AdminTournaments from "./pages/admin/AdminTournaments";
import AdminCTAs from "./pages/admin/AdminCTAs";
import AdminContacts from "./pages/admin/AdminContacts";
import AdminNutsProgram from "./pages/admin/AdminNutsProgram";
import AdminPremsa from "./pages/admin/AdminPremsa";
import AdminInicicioAnyos from "./pages/admin/AdminInicicioAnyos";
import AdminMinisites from "./pages/admin/AdminMinisites";
import AdminMinisiteEditor from "./pages/admin/AdminMinisiteEditor";
import AdminSurvey from "./pages/admin/AdminSurvey";
import AdminWhatsappAutoreplies from "./pages/admin/AdminWhatsappAutoreplies";
import AdminSoldeuLanding from "./pages/admin/AdminSoldeuLanding";
import AdminSigLanding from "./pages/admin/AdminSigLanding";
import AdminTicker from "./pages/admin/AdminTicker";
import NewsTicker from "./components/NewsTicker";
import MenuRestaurantPage from "./pages/MenuRestaurantPage";
import SoldeuLandingPage from "./pages/SoldeuLandingPage";
import SigLandingPage from "./pages/SigLandingPage";

// Public pages
import NutsProgramPage from "./pages/NutsProgramPage";
import PremsaPage from "./pages/PremsaPage";
import InicicioAnyosPage from "./pages/InicicioAnyosPage";
import AnyosParkPage from "./pages/AnyosParkPage";
import CareersPage from "./pages/CareersPage";
import MinisitePage from "./pages/MinisitePage";
import MinisiteWrapper from "./components/MinisiteWrapper";
import SurveyPage from "./pages/SurveyPage";

// Protected Route wrapper
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9F9F7]">
        <Loader2 className="w-8 h-8 animate-spin text-[#1A4D2E]" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/admin/login" state={{ from: location }} replace />;
  }

  return children;
};

// App Router component to handle auth callback detection
const AppRouter = () => {
  const location = useLocation();
  
  // Check URL fragment for session_id (from Google Auth callback)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Admin routes - no header/footer */}
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin/auth-callback" element={<AuthCallback />} />
      <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
      <Route path="/admin/users" element={<ProtectedRoute><AdminUsers /></ProtectedRoute>} />
      <Route path="/admin/pages" element={<ProtectedRoute><AdminPages /></ProtectedRoute>} />
      <Route path="/admin/pages/:pageKey/:lang" element={<ProtectedRoute><PageEditor /></ProtectedRoute>} />
      <Route path="/admin/sections" element={<ProtectedRoute><AdminSections /></ProtectedRoute>} />
      <Route path="/admin/sections/:sectionKey" element={<ProtectedRoute><AdminSectionEditor /></ProtectedRoute>} />
      <Route path="/admin/config" element={<ProtectedRoute><AdminSiteConfig /></ProtectedRoute>} />
      <Route path="/admin/home-content" element={<ProtectedRoute><AdminHomeContent /></ProtectedRoute>} />
      <Route path="/admin/carousel" element={<ProtectedRoute><AdminCarousel /></ProtectedRoute>} />
      <Route path="/admin/tournaments" element={<ProtectedRoute><AdminTournaments /></ProtectedRoute>} />
      <Route path="/admin/ctas" element={<ProtectedRoute><AdminCTAs /></ProtectedRoute>} />
      <Route path="/admin/holes" element={<ProtectedRoute><AdminHoles /></ProtectedRoute>} />
      <Route path="/admin/documents" element={<ProtectedRoute><AdminDocuments /></ProtectedRoute>} />
      <Route path="/admin/media" element={<ProtectedRoute><AdminMedia /></ProtectedRoute>} />
      <Route path="/admin/contacts" element={<ProtectedRoute><AdminContacts /></ProtectedRoute>} />
      <Route path="/admin/programa-nuts" element={<ProtectedRoute><AdminNutsProgram /></ProtectedRoute>} />
      <Route path="/admin/premsa" element={<ProtectedRoute><AdminPremsa /></ProtectedRoute>} />
      <Route path="/admin/iniciacio-anyos" element={<ProtectedRoute><AdminInicicioAnyos /></ProtectedRoute>} />
      <Route path="/admin/minisites" element={<ProtectedRoute><AdminMinisites /></ProtectedRoute>} />
      <Route path="/admin/minisites/:id" element={<ProtectedRoute><AdminMinisiteEditor /></ProtectedRoute>} />
      <Route path="/admin/enquestes" element={<ProtectedRoute><AdminSurvey /></ProtectedRoute>} />
      <Route path="/admin/whatsapp-autoreplies" element={<ProtectedRoute><AdminWhatsappAutoreplies /></ProtectedRoute>} />
      <Route path="/admin/soldeu-landing" element={<ProtectedRoute><AdminSoldeuLanding /></ProtectedRoute>} />
      <Route path="/admin/sig-landing" element={<ProtectedRoute><AdminSigLanding /></ProtectedRoute>} />
      <Route path="/admin/ticker" element={<ProtectedRoute><AdminTicker /></ProtectedRoute>} />

      {/* Standalone public pages (no header/footer) */}
      <Route path="/menu-restaurant" element={<MenuRestaurantPage />} />
      <Route path="/menu-restaurant/" element={<MenuRestaurantPage />} />
      <Route path="/Iniciacio-a-Soldeu" element={<SoldeuLandingPage />} />
      <Route path="/curs-iniciacio-golf" element={<SigLandingPage />} />

      {/* Public routes with header/footer */}
      <Route path="/*" element={<PublicLayout />} />
    </Routes>
  );
};

// Valid language check wrapper
const ValidLangRoute = ({ element }) => {
  const { lang } = useParams();
  const validLangs = ['ca', 'es', 'fr', 'en'];
  
  if (!validLangs.includes(lang)) {
    return <NotFoundPage />;
  }
  
  return element;
};

// Public layout with header and footer
const PublicLayout = () => {
  return (
    <>
      <Header />
      <main className="flex-1">
        <Routes>
          {/* Home */}
          <Route path="/" element={<HomePage />} />
          
          {/* Main sections */}
          <Route path="/el-camp" element={<ElCampPage />} />
          <Route path="/jugar" element={<JugarPage />} />
          <Route path="/competicio" element={<CompeticioPage />} />
          <Route path="/iniciacio" element={<AcademiaPage />} />
          <Route path="/driving-range" element={<DrivingRangePage />} />
          <Route path="/restaurant" element={<RestaurantPage />} />
          <Route path="/allotjament" element={<AllotjamentPage />} />
          <Route path="/correspondencies" element={<CorrespondenciesPage />} />
          <Route path="/contacte" element={<ContactePage />} />
          <Route path="/equip" element={<EquipPage />} />
          <Route path="/membres" element={<MembresPage />} />
          <Route path="/fes-te-abonat" element={<FesTeAbonatPage />} />
          <Route path="/programa-nuts" element={<NutsProgramPage />} />
          <Route path="/premsa" element={<PremsaPage />} />
          <Route path="/iniciacio-anyos" element={<InicicioAnyosPage />} />
          <Route path="/anyos-park" element={<MinisiteWrapper customUrl="anyos-park" fallbackComponent={AnyosParkPage} />} />
          <Route path="/careers" element={<CareersPage />} />
          <Route path="/feina" element={<CareersPage />} />
          <Route path="/rrhh" element={<CareersPage />} />
          <Route path="/enquesta" element={<SurveyPage />} />
          
          {/* Legal pages */}
          <Route path="/legal/:slug" element={<LegalPage />} />
          
          {/* Language prefixed routes - only valid langs */}
          <Route path="/:lang" element={<ValidLangRoute element={<HomePage />} />} />
          <Route path="/:lang/el-camp" element={<ValidLangRoute element={<ElCampPage />} />} />
          <Route path="/:lang/jugar" element={<ValidLangRoute element={<JugarPage />} />} />
          <Route path="/:lang/competicio" element={<ValidLangRoute element={<CompeticioPage />} />} />
          <Route path="/:lang/iniciacio" element={<ValidLangRoute element={<AcademiaPage />} />} />
          <Route path="/:lang/driving-range" element={<ValidLangRoute element={<DrivingRangePage />} />} />
          <Route path="/:lang/restaurant" element={<ValidLangRoute element={<RestaurantPage />} />} />
          <Route path="/:lang/allotjament" element={<ValidLangRoute element={<AllotjamentPage />} />} />
          <Route path="/:lang/correspondencies" element={<ValidLangRoute element={<CorrespondenciesPage />} />} />
          <Route path="/:lang/contacte" element={<ValidLangRoute element={<ContactePage />} />} />
          <Route path="/:lang/equip" element={<ValidLangRoute element={<EquipPage />} />} />
          <Route path="/:lang/membres" element={<ValidLangRoute element={<MembresPage />} />} />
          <Route path="/:lang/fes-te-abonat" element={<ValidLangRoute element={<FesTeAbonatPage />} />} />
          <Route path="/:lang/programa-nuts" element={<ValidLangRoute element={<NutsProgramPage />} />} />
          <Route path="/:lang/premsa" element={<ValidLangRoute element={<PremsaPage />} />} />
          <Route path="/:lang/careers" element={<ValidLangRoute element={<CareersPage />} />} />
          <Route path="/:lang/feina" element={<ValidLangRoute element={<CareersPage />} />} />
          <Route path="/:lang/legal/:slug" element={<ValidLangRoute element={<LegalPage />} />} />
          
          {/* Minisites - dynamic pages (must be before 404) */}
          <Route path="/m/:slug" element={<MinisitePage />} />
          
          {/* 404 catch-all */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </main>
      <Footer />
      <WhatsAppButton />
      <NewsTicker />
      <CookieBanner />
    </>
  );
};

function App() {
  return (
    <LanguageProvider>
      <AuthProvider>
        <div className="App min-h-screen flex flex-col">
          <BrowserRouter>
            <ScrollToTop />
            <AppRouter />
          </BrowserRouter>
          <Toaster position="top-right" />
        </div>
      </AuthProvider>
    </LanguageProvider>
  );
}

export default App;
